import { useId } from "react";

type Segment = {
  value: number;
  color: string;
  label?: string;
};

type OperationsBarProps = {
  width?: number;
  height?: number;
  segments: Segment[]; // order matters: will render left->right
};

export const OperationsBar = ({ width = 80, height = 10, segments }: OperationsBarProps) => {
  const total = segments.reduce((sum, s) => sum + (s.value || 0), 0);
  const barHeight = height;
  const textY = -2; // above the bar
  const radius = Math.min(6, Math.floor(barHeight / 2));
  const uid = useId();

  let x = 0;

  // Material color gradients (400 -> 600 variants)
  const gradients: Record<string, { id: string; from: string; to: string }> = {
    failed: {
      id: `opsbar-failed-${uid}`,
      from: "#EF5350", // red 400
      to: "#E53935", // red 600
    },
    succeeded: {
      id: `opsbar-succeeded-${uid}`,
      from: "#66BB6A", // green 400
      to: "#43A047", // green 600
    },
    canceled: {
      id: `opsbar-canceled-${uid}`,
      from: "#FFA726", // orange 400
      to: "#FB8C00", // orange 600
    },
  };

  return (
    <svg width={width} height={barHeight + 12} style={{ overflow: "visible" }}>
      <defs>
        {Object.values(gradients).map((g) => (
          <linearGradient id={g.id} key={g.id} x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor={g.from} />
            <stop offset="100%" stopColor={g.to} />
          </linearGradient>
        ))}
        <clipPath id={`opsbar-clip-${uid}`}>
          <rect x={0} y={0} width={width} height={barHeight} rx={radius} ry={radius} />
        </clipPath>
      </defs>
      <g transform={`translate(0, 8)`}>
        {total === 0 ? (
          <rect x={0} y={0} width={width} height={barHeight} rx={radius} ry={radius} fill="#EEE" stroke="#CCC" />
        ) : (
          (() => {
            // Precompute segment positions
            const parts = segments.map((seg) => {
              const segWidth = total === 0 ? 0 : Math.max(0, Math.round((seg.value / total) * width));
              const part = { seg, start: x, width: segWidth };
              x += segWidth;
              return part;
            });

            return (
              <>
                {/* Bars with rounded clipping */}
                <g clipPath={`url(#opsbar-clip-${uid})`}>
                  {parts.map(({ seg, start, width: w }, idx) => {
                    const gradient = seg.label && gradients[seg.label];
                    const fill = gradient ? `url(#${gradient.id})` : seg.color;
                    return (
                      <rect key={`rect-${idx}`} x={start} y={0} width={w} height={barHeight} fill={fill} />
                    );
                  })}
                </g>
                {/* Labels above bar (not clipped) */}
                {parts.map(({ seg, start, width: w }, idx) =>
                  seg.value > 0 ? (
                    <text
                      key={`text-${idx}`}
                      x={start + w / 2}
                      y={textY}
                      textAnchor="middle"
                      fontSize={10}
                      fill={seg.label && gradients[seg.label] ? gradients[seg.label!].to : seg.color}
                      style={{ pointerEvents: "none" }}
                    >
                      {seg.value}
                    </text>
                  ) : null,
                )}
              </>
            );
          })()
        )}
      </g>
    </svg>
  );
};

export default OperationsBar;


